<template>
  <section class="members">
    <v-progress-circular
      v-if="isEventLoading"
      class="loader"
      color="#FC7800"
      indeterminate
      size="280"
      width="15"
    />
    <div
      v-else
      class="content"
    >
      <div class="list">
        <!--          eslint-disable vuejs-accessibility/click-events-have-key-events-->
        <span
          class="back-button back-button--mobile"
          @click="backToEvent"
        >
          <v-icon>{{ mdiChevronLeft }}</v-icon> Back
        </span>
        <!--          eslint-enable vuejs-accessibility/click-events-have-key-events-->
        <h2 class="checkin-title page-title">Check people in</h2>
        <h3 class="dancers-count">{{ participantsCheckedInCount }}/{{ participantsCount }} checked in</h3>

        <v-text-field
          v-model="searchKeyword"
          background-color="var(--color-white)"
          hide-details
          label="Search people"
          outlined
        />
        <v-row class="list-header">
          <v-col
            class="item"
            cols="5"
          >
            User
          </v-col>

          <v-col
            class="item"
            cols="2"
          >
            Battles
          </v-col>

          <v-col
            class="item status"
            cols="5"
          >
            Status
          </v-col>
        </v-row>

        <template v-if="isAttendeesLoading">
          <v-skeleton-loader
            v-for="n in 5"
            :key="n"
            class="participant-skeleton"
            type="list-item-avatar"
          />
        </template>

        <div
          v-else-if="isAttendeesError"
          class="attendees-error"
        >
          <p>Failed to load attendees</p>
          <BKButton
            :loading="isRefetching"
            color="var(--color-orange-500)"
            dark
            small
            width="fit-content"
            @click="refetchAttendees"
          >
            Try again
          </BKButton>
        </div>

        <template v-else>
          <CheckInParticipantsRow
            v-for="participant in participants"
            :key="participant.id"
            :event="event"
            :is-free="event?.isFree ?? true"
            :participant="participant"
            @update:event="refetchAttendees"
          />
          <p
            v-if="participants.length === 0"
            class="empty-state"
          >
            No participants found
          </p>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { mdiChevronLeft } from '@mdi/js'
import CheckInParticipantsRow from '@/components/CheckInParticipantsRow.vue'
import { useEventQuery, useEventAttendeesQuery } from '@/enitites/event/api/queries'
import { useVuetify } from '@/services/useVuetify'
// @ts-expect-error not ts file
import store from '@/store'

const route = useRoute()
const router = useRouter()
const vuetify = useVuetify()

const eventId = Number(route.params.id)

const { data: event, isLoading: isEventLoading } = useEventQuery(eventId)
const {
  data: attendees,
  isLoading: isAttendeesLoading,
  isError: isAttendeesError,
  isFetching: isRefetching,
  refetch: refetchAttendees,
} = useEventAttendeesQuery(eventId)

const searchKeyword = ref('')
const isShowLegend = ref(false)

watch(
  [isEventLoading, event],
  ([isLoading, data]) => {
    if (!isLoading && !data) store.commit('setIsNotFound', true)
  },
  { immediate: true }
)

onMounted(() => {
  if (vuetify.breakpoint.mdAndUp) isShowLegend.value = true
})

const participants = computed(() => {
  if (!attendees.value) return []
  if (!searchKeyword.value) return attendees.value
  return attendees.value.filter((a) => a.dancerName.toLowerCase().includes(searchKeyword.value.toLowerCase()))
})

const participantsCount = computed(() => attendees.value?.length ?? 0)

const participantsCheckedInCount = computed(() => attendees.value?.filter((a) => a.checkedIn).length ?? 0)

const backToEvent = () => {
  router.push({ name: 'Event', params: { id: String(eventId) } })
}
</script>

<style lang="scss">
.v-application--is-ltr .tier-item .v-input--selection-controls__input {
  margin-right: 0 !important;
}
</style>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.loader {
  display: flex !important;
  margin: 210px auto;
}

.members {
  background-color: var(--color-grey-100) !important;
  padding-bottom: 64px;
}

.content {
  .content {
    @include page-with-content;
  }

  position: relative;
  display: flex;
  max-width: 1100px;
  margin: 180px auto 0;

  @media (max-width: $screen-md) {
    flex-direction: column-reverse;
    margin-top: 100px;
    padding: 0 20px;
  }

  .list {
    flex-grow: 1;
    margin-right: 40px;

    @media (max-width: $screen-md) {
      margin-right: 0;
    }
  }

  .legend {
    width: 100%;
    max-width: 305px;
    height: fit-content;
    background-color: var(--color-white);

    @media (max-width: $screen-md) {
      max-width: unset;
      margin-bottom: 20px;
    }
  }
}

.back-button {
  position: absolute;
  top: 120px;
  right: 77%;
  font-weight: var(--font-weight-extra-bold);
  cursor: pointer;

  &--mobile {
    position: static;
  }
}

.member-link {
  color: black;
}

.checked-label {
  margin-left: auto;
  color: var(--color-green);

  .v-icon {
    color: var(--color-green);
  }
}

.card {
  position: relative;
  height: 100%;
  min-height: 570px;
  margin: 120px auto;
}

.checkin-title {
  @include font-h1;
}

.dancers-count {
  @include font-h3;

  margin: 12px 0;
}

.list-header {
  margin-top: 24px;

  @media (max-width: $screen-md) {
    display: none;
  }

  .item {
    @include font-body2;

    font-weight: var(--font-weight-bold);
  }

  .status {
    text-align: end;
  }
}

.participant-skeleton {
  margin-bottom: 4px;
}

.attendees-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 0;
  color: var(--color-grey-300);
}

.empty-state {
  padding: 40px 0;
  text-align: center;
  color: var(--color-grey-300);
}
</style>
